import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import * as dealsAPI from '../../../apis/dealsAPI'
import type { DealEntity, ListPaginatedDealsResult } from '../../../apis/dealsAPI'
import { useApiErrorHandler } from '../../../hooks/useApiErrorHandler'
import { useToast } from '../../../hooks/useToast'

import { DEALS_QUERY_KEY_ROOT } from './useDealsQuery'

interface ArchiveVariables {
  dealId: string
  /** When true, restores an archived deal back to the active list. */
  restore?: boolean
}

interface OptimisticContext {
  /** Snapshots of every active list-query cache, keyed by their query key. */
  snapshots: Array<{ key: readonly unknown[]; data: ListPaginatedDealsResult | undefined }>
  archivedAt: string | null
}

function withArchivedFlag(deal: DealEntity, archivedAt: string | null): DealEntity {
  return { ...deal, archivedAt }
}

export function useArchiveDealMutation(): UseMutationResult<
  DealEntity,
  unknown,
  ArchiveVariables,
  OptimisticContext
> {
  const queryClient = useQueryClient()
  const toast = useToast()
  const handleError = useApiErrorHandler()

  return useMutation<DealEntity, unknown, ArchiveVariables, OptimisticContext>(
    ({ dealId, restore }) => (restore ? dealsAPI.unarchiveDeal(dealId) : dealsAPI.archiveDeal(dealId)),
    {
      onMutate: async ({ dealId, restore }): Promise<OptimisticContext> => {
        await queryClient.cancelQueries({ queryKey: DEALS_QUERY_KEY_ROOT })

        const archivedAt = restore ? null : new Date().toISOString()
        const snapshots: OptimisticContext['snapshots'] = []

        const queries = queryClient.getQueriesData<ListPaginatedDealsResult>({
          queryKey: DEALS_QUERY_KEY_ROOT,
        })
        for (const [key, data] of queries) {
          // useInvoicesQuery / useDealQuery share the `['deals']` root but
          // expose different payload shapes; skip anything without `.deals`.
          if (!data || !Array.isArray((data as ListPaginatedDealsResult).deals)) continue
          snapshots.push({ key, data })
          const nextDeals = data.deals.map((deal) =>
            deal.id === dealId ? withArchivedFlag(deal, archivedAt) : deal,
          )
          queryClient.setQueryData<ListPaginatedDealsResult>(key, {
            ...data,
            deals: nextDeals,
          })
        }

        return { snapshots, archivedAt }
      },
      onError: (error, _variables, context) => {
        if (context) {
          for (const snapshot of context.snapshots) {
            queryClient.setQueryData(snapshot.key, snapshot.data)
          }
        }
        handleError(error)
      },
      onSuccess: (_deal, { restore }) => {
        toast.current?.show({
          severity: 'info',
          summary: restore ? 'Deal restored' : 'Deal archived',
          detail: restore
            ? 'The deal is back in the active list.'
            : 'Find this deal under Status: Archived.',
          life: 3000,
        })
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: DEALS_QUERY_KEY_ROOT })
      },
    },
  )
}
